import React, { useEffect, useState } from 'react';
import { useFeedback } from '../../contexts/FeedbackContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
    MessageSquare,
    Bug,
    Lightbulb,
    CheckCircle2,
    Trash2,
    ExternalLink,
    Search,
    Filter,
    Clock,
    User,
    AlertTriangle,
    Save,
    Power
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const AdminFeedbacks = () => {
    const { feedbacks, markAsResolved, deleteFeedback, refreshFeedbacks } = useFeedback();
    const [filter, setFilter] = useState('open'); // all, open, resolved
    const [search, setSearch] = useState('');
    const [lastUpdated, setLastUpdated] = useState(new Date().toLocaleTimeString('pt-BR'));
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Banner Settings State
    const [bannerConfig, setBannerConfig] = useState({
        active: false,
        message: '',
        type: 'warning' // warning, error, info
    });
    const [loadingBanner, setLoadingBanner] = useState(false);

    useEffect(() => {
        fetchBannerSettings();
    }, []);

    const fetchBannerSettings = async () => {
        try {
            const { data, error } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'global_banner')
                .single();

            if (data?.value) {
                setBannerConfig(data.value);
            }
        } catch (error) {
            console.error('Error loading banner:', error);
        }
    };

    const saveBannerSettings = async () => {
        setLoadingBanner(true);
        try {
            const { data, error } = await supabase
                .from('system_settings')
                .upsert({
                    key: 'global_banner',
                    value: bannerConfig,
                    updated_at: new Date().toISOString()
                }, { onConflict: 'key' })
                .select();

            if (data && data.length === 0) {
                console.warn("Update returned 0 rows. Row might not exist or RLS blocked it.");
                // Fallback: Try insert if update ignored (careful with RLS)
            }

            if (error) throw error;
            toast.success('Configuração do aviso global atualizada!');
        } catch (error) {
            console.error(error);
            toast.error('Erro ao salvar configuração.');
        } finally {
            setLoadingBanner(false);
        }
    };

    // Real-time updates handled by context


    const filteredFeedbacks = feedbacks.filter(f => {
        const matchesFilter = filter === 'all' || f.status === filter || (filter === 'open' && f.status !== 'resolved');

        const messageMatch = f.message?.toLowerCase().includes(search.toLowerCase()) || false;
        const nameMatch = f.user?.name?.toLowerCase().includes(search.toLowerCase()) || false;
        const typeMatch = f.type?.toLowerCase().includes(search.toLowerCase()) || false;

        const matchesSearch = messageMatch || nameMatch || typeMatch;
        return matchesFilter && matchesSearch;
    });

    const getTypeIcon = (type) => {
        switch (type) {
            case 'bug': return <Bug className="h-4 w-4 text-red-400" />;
            case 'suggestion': return <Lightbulb className="h-4 w-4 text-amber-400" />;
            default: return <MessageSquare className="h-4 w-4 text-blue-400" />;
        }
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'bug': return 'Erro/Bug';
            case 'suggestion': return 'Sugestão';
            default: return 'Geral';
        }
    };

    const handleResolve = async (id) => {
        const result = await markAsResolved(id);
        if (result.success) {
            toast.success('Feedback marcado como resolvido!');
            await refreshFeedbacks();
            setLastUpdated(new Date().toLocaleTimeString('pt-BR'));
        } else {
            toast.error(result.error || 'Erro ao resolver feedback.');
        }
    };

    const handleDelete = async (id) => {
        if (confirm('Tem certeza que deseja excluir este feedback?')) {
            await deleteFeedback(id);
            toast.success('Feedback excluído com sucesso!');
            await refreshFeedbacks();
            setLastUpdated(new Date().toLocaleTimeString('pt-BR'));
        }
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await refreshFeedbacks();
            setLastUpdated(new Date().toLocaleTimeString('pt-BR'));
            toast.success('Feedbacks atualizados!');
        } catch (error) {
            toast.error('Erro ao atualizar feedbacks');
        } finally {
            setIsRefreshing(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white mb-1">Feedbacks do Sistema</h1>
                    <p className="text-gray-400">Gerencie reportes de erros e sugestões dos usuários</p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="text-right">
                        <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Última atualização</p>
                        <p className="text-xs text-gray-400 font-mono">{lastUpdated}</p>
                    </div>
                    <Button
                        variant="outline"
                        size="icon"
                        className={`bg-gray-900/50 border-gray-800 hover:bg-gray-800 transition-all ${isRefreshing ? 'animate-spin' : ''}`}
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                    >
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </Button>
                </div>
            </div>

            {/* Global Banner Management */}
            <div className="bg-gray-900/50 p-6 rounded-xl border border-gray-800 space-y-4">
                <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    <h2 className="text-lg font-bold text-white">Aviso Global / Manutenção</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div className="md:col-span-2 space-y-2">
                        <label className="text-sm text-gray-400">Mensagem do Aviso</label>
                        <input
                            type="text"
                            value={bannerConfig.message}
                            onChange={(e) => setBannerConfig({ ...bannerConfig, message: e.target.value })}
                            placeholder="Ex: Estamos em manutenção..."
                            className="w-full bg-gray-950 border border-gray-800 rounded-lg p-2.5 text-white focus:ring-1 focus:ring-violet-500"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm text-gray-400">Tipo de Alerta</label>
                        <select
                            value={bannerConfig.type}
                            onChange={(e) => setBannerConfig({ ...bannerConfig, type: e.target.value })}
                            className="w-full bg-gray-950 border border-gray-800 rounded-lg p-2.5 text-white focus:ring-1 focus:ring-violet-500"
                        >
                            <option value="info">Informativo (Azul)</option>
                            <option value="warning">Atenção (Amarelo)</option>
                            <option value="error">Crítico / Manutenção (Vermelho)</option>
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <Button
                            onClick={() => setBannerConfig({ ...bannerConfig, active: !bannerConfig.active })}
                            className={`flex-1 border-0 ${bannerConfig.active ? 'bg-red-500/20 text-red-500 hover:bg-red-500/30' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
                        >
                            <Power className="h-4 w-4 mr-2" />
                            {bannerConfig.active ? 'Desativar' : 'Ativar'}
                        </Button>
                        <Button
                            onClick={saveBannerSettings}
                            disabled={loadingBanner}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white border-0"
                        >
                            <Save className="h-4 w-4 mr-2" />
                            Salvar
                        </Button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 bg-gray-900/50 p-4 rounded-xl border border-gray-800">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <input
                        type="text"
                        placeholder="Buscar por mensagem, usuário..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full bg-gray-950 border border-gray-800 rounded-lg pl-9 pr-4 py-2 text-sm text-gray-200 focus:outline-none focus:border-violet-500/50"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setFilter('all')}
                        className={`${filter === 'all' ? 'bg-gray-800 text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                        Todos
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setFilter('open')}
                        className={`${filter === 'open' ? 'bg-gray-800 text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                        Abertos
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setFilter('resolved')}
                        className={`${filter === 'resolved' ? 'bg-gray-800 text-green-400' : 'text-gray-400 hover:text-white'}`}
                    >
                        Resolvidos
                    </Button>
                </div>
            </div>

            {/* List */}
            <div className="grid gap-4">
                <AnimatePresence>
                    {filteredFeedbacks.map((item) => (
                        <motion.div
                            key={item.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className={`bg-gray-900/50 border rounded-xl p-4 transition-all ${item.status === 'resolved' ? 'border-green-900/30 opacity-75' : 'border-gray-800 hover:border-violet-500/30'
                                }`}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-4 flex-1">
                                    <div className={`p-2 rounded-lg shrink-0 ${item.type === 'bug' ? 'bg-red-500/10' :
                                        item.type === 'suggestion' ? 'bg-amber-500/10' : 'bg-blue-500/10'
                                        }`}>
                                        {getTypeIcon(item.type)}
                                    </div>
                                    <div className="space-y-2 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${item.type === 'bug' ? 'bg-red-500/5 border-red-500/20 text-red-400' :
                                                item.type === 'suggestion' ? 'bg-amber-500/5 border-amber-500/20 text-amber-400' : 'bg-blue-500/5 border-blue-500/20 text-blue-400'
                                                }`}>
                                                {getTypeLabel(item.type)}
                                            </span>
                                            {item.status === 'resolved' && (
                                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 flex items-center gap-1">
                                                    <CheckCircle2 className="h-3 w-3" />
                                                    Resolvido
                                                </span>
                                            )}
                                            <span className="text-xs text-gray-500 flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {new Date(item.createdat || item.created_at || item.createdAt || parseInt(item.id)).toLocaleString()}
                                            </span>
                                        </div>

                                        <p className="text-gray-200 text-sm leading-relaxed whitespace-pre-wrap">
                                            {item.message}
                                        </p>

                                        <div className="flex items-center gap-4 text-xs text-gray-500 mt-2 bg-black/20 p-2 rounded-lg w-full sm:w-fit">
                                            <div className="flex items-center gap-1">
                                                <User className="h-3 w-3" />
                                                <span className={item.user.role === 'admin' ? 'text-red-400' : 'text-gray-400'}>
                                                    {item.user.name} ({item.user.role})
                                                </span>
                                            </div>
                                            <div className="h-3 w-px bg-gray-700"></div>
                                            <a href={item.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-violet-400 transition-colors">
                                                <ExternalLink className="h-3 w-3" />
                                                Ver página
                                            </a>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2">
                                    {item.status !== 'resolved' && (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleResolve(item.id)}
                                            className="text-gray-400 hover:text-green-400 hover:bg-green-500/10"
                                            title="Marcar como resolvido"
                                        >
                                            <CheckCircle2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDelete(item.id)}
                                        className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                                        title="Excluir feedback"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>

                {filteredFeedbacks.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                        <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-20" />
                        <p>Nenhum feedback encontrado.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminFeedbacks;
